import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Send, Check, X, PenTool, CheckCircle2, RotateCcw } from 'lucide-react';
import { contractApi, contractTemplateApi } from '../../api/contracts';
import { genericDocumentApi } from '../../api/generic-documents';
import { ApiError } from '../../api/client';
import { RecordHeader } from '../../components/RecordHeader';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { StatusBadge } from '../../components/StatusBadge';
import { CommentsThread } from '../../components/CommentsThread';
import { DocumentsSection } from '../../components/DocumentsSection';
import { AuditHistoryTab } from '../../components/AuditHistoryTab';
import { WorkflowTrackingTab } from '../../components/WorkflowTrackingTab';
import { CONTRACT_STAGES } from '../../config/workflow-stages';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { useAuth, usePermission } from '../../auth/AuthContext';
import { formatDate } from '../../utils/formatDate';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', hr_review: 'HR Review', returned: 'Returned for Redraft', approved: 'Approved',
  signed: 'Signed', finalised: 'Finalised', expired: 'Expired', terminated: 'Terminated', rejected: 'Rejected',
};

/**
 * Contract Workspace architecture - a real, standalone workspace at
 * /hr/contracts/:id, replacing the section that used to be buried
 * inside StaffDetailPage. Every workflow action that used to live
 * there (edit, submit, request approval, decide, sign, finalise,
 * download) is here, contextual to the real current status - nothing
 * was dropped in the move.
 */
export function ContractWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const contractId = Number(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const canManage = usePermission('contracts.manage');

  const [signatureAction, setSignatureAction] = useState<'sign' | 'finalise' | null>(null);
  const [typedName, setTypedName] = useState('');
  const [returnComment, setReturnComment] = useState('');
  const [showReturnForm, setShowReturnForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editValues, setEditValues] = useState({ templateId: '', startDate: '', endDate: '' });

  const contractQuery = useQuery({ queryKey: ['contract', contractId], queryFn: () => contractApi.get(contractId) });
  const docsQuery = useQuery({ queryKey: ['contract-documents', contractId], queryFn: () => genericDocumentApi.listFor('contract', contractId) });
  const templatesQuery = useQuery({ queryKey: ['contract-templates'], queryFn: () => contractTemplateApi.list(), enabled: showEditForm });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['contract', contractId] });
  const onError = (err: unknown) => showError(err instanceof ApiError ? err.message : 'Something went wrong.');

  const submitMutation = useMutation({ mutationFn: () => contractApi.submit(contractId), onSuccess: () => { showSuccess('Submitted for HR review.'); invalidate(); }, onError });
  const requestApprovalMutation = useMutation({ mutationFn: () => contractApi.requestApproval(contractId), onSuccess: () => { showSuccess('Approval requested.'); invalidate(); }, onError });
  const decideMutation = useMutation({ mutationFn: (decision: 'approved' | 'rejected') => contractApi.decide(contractId, { decision }), onSuccess: () => { showSuccess('Decision recorded.'); invalidate(); }, onError });
  const resubmitMutation = useMutation({ mutationFn: () => contractApi.resubmit(contractId), onSuccess: () => { showSuccess('Resubmitted.'); invalidate(); }, onError });
  /**
   * Real gap closed (Delete capability request) - genuinely destructive
   * and irreversible: removes the contract, its comments, documents,
   * AND its full tracking/audit history. Works on any status, per
   * explicit direction, including signed/finalised - the confirmation
   * dialog is the only safeguard, so it names exactly what's about to
   * be lost.
   */
  const deleteMutation = useMutation({
    mutationFn: () => contractApi.delete(contractId),
    onSuccess: () => { showSuccess('Contract deleted.'); navigate('/hr/contracts'); },
    onError,
  });
  const handleDelete = () => {
    if (window.confirm('Permanently delete this contract? This also deletes its comments, documents, and full tracking/audit history. This cannot be undone.')) {
      deleteMutation.mutate();
    }
  };

  const returnMutation = useMutation({
    mutationFn: () => contractApi.return(contractId, returnComment),
    onSuccess: () => { showSuccess('Returned for redraft.'); invalidate(); setShowReturnForm(false); setReturnComment(''); },
    onError,
  });
  /** Edit lives directly in the workspace now, not a redirect back to the employee's own page - consistent with how every other entity in VConnect handles edit-in-place. */
  const updateMutation = useMutation({
    mutationFn: () => contractApi.update(contractId, { templateId: editValues.templateId ? Number(editValues.templateId) : null, startDate: editValues.startDate, endDate: editValues.endDate || null }),
    onSuccess: () => { showSuccess('Contract updated - the document has been regenerated with the new values.'); invalidate(); setShowEditForm(false); },
    onError,
  });

  const signMutation = useMutation({ mutationFn: (typedFullName: string) => contractApi.sign(contractId, typedFullName), onSuccess: () => { showSuccess('Signed - your typed name and the date are now recorded on the document.'); invalidate(); closeSignatureModal(); }, onError });
  const finaliseMutation = useMutation({ mutationFn: (typedFullName: string) => contractApi.finalise(contractId, typedFullName), onSuccess: () => { showSuccess('Contract finalised - employee record updated and the organization\'s signature is recorded.'); invalidate(); closeSignatureModal(); }, onError });

  const openSignatureModal = (action: 'sign' | 'finalise') => { setSignatureAction(action); setTypedName(user?.fullName ?? ''); };
  const closeSignatureModal = () => { setSignatureAction(null); setTypedName(''); };
  const submitSignature = () => {
    if (typedName.trim().length < 2) return;
    if (signatureAction === 'sign') signMutation.mutate(typedName.trim());
    else finaliseMutation.mutate(typedName.trim());
  };

  if (!contractQuery.data) return <div className="page"><p className="muted">Loading…</p></div>;
  const c = contractQuery.data.data;

  return (
    <div className="page">
      <Link to="/hr/contracts">&larr; Back to Contracts</Link>

      <RecordHeader
        title={`Contract - ${c.contract_reference}`}
        entityType="contract"
        entityId={contractId}
        readOnly={!canManage}
        fields={[
          { label: 'Employee', value: <Link to={`/hr/staff/${c.employee_id}`}>{c.employee_name ?? '-'}</Link> },
          { label: 'Employment Type', value: c.employment_type ?? '-' },
          { label: 'Start Date', value: formatDate(c.start_date) },
          { label: 'End Date', value: c.end_date ? formatDate(c.end_date) : 'Open-ended' },
          { label: 'Status', value: <StatusBadge status={c.status} label={STATUS_LABELS[c.status]} /> },
        ]}
        actions={canManage && (
          <span className="row-actions">
            {['draft', 'returned'].includes(c.status) && <button className="btn-secondary" onClick={() => { setEditValues({ templateId: c.template_id ? String(c.template_id) : '', startDate: c.start_date, endDate: c.end_date ?? '' }); setShowEditForm(true); }}>Edit</button>}
            {c.status === 'draft' && <button className="btn-primary" onClick={() => submitMutation.mutate()} disabled={submitMutation.isPending}><Send size={16} className="inline-icon" /> Submit for Review</button>}
            {c.status === 'hr_review' && !c.approval_requested && <button className="btn-primary" onClick={() => requestApprovalMutation.mutate()} disabled={requestApprovalMutation.isPending}>Request Approval</button>}
            {c.status === 'hr_review' && !!c.approval_requested && (
              <>
                <button className="btn-primary" onClick={() => decideMutation.mutate('approved')} disabled={decideMutation.isPending}><Check size={16} className="inline-icon" /> Approve</button>
                <button className="button-danger" onClick={() => decideMutation.mutate('rejected')} disabled={decideMutation.isPending}><X size={16} className="inline-icon" /> Reject</button>
                <button className="btn-secondary" onClick={() => setShowReturnForm((v) => !v)}><RotateCcw size={16} className="inline-icon" /> Return for Redraft</button>
              </>
            )}
            {c.status === 'returned' && <button className="btn-primary" onClick={() => resubmitMutation.mutate()} disabled={resubmitMutation.isPending}>Resubmit</button>}
            {c.status === 'approved' && <button className="btn-primary" onClick={() => openSignatureModal('sign')}><PenTool size={16} className="inline-icon" /> Sign</button>}
            {c.status === 'signed' && <button className="btn-primary" onClick={() => openSignatureModal('finalise')}><CheckCircle2 size={16} className="inline-icon" /> Finalise</button>}
            {c.generated_content && <a className="btn-secondary" href={contractApi.downloadUrl(contractId)}>Download</a>}
            <button className="button-danger" onClick={handleDelete} disabled={deleteMutation.isPending}>{deleteMutation.isPending ? 'Deleting…' : 'Delete'}</button>
          </span>
        )}
      />

      {c.status === 'returned' && c.return_comment && (
        <p className="error-text">Returned: {c.return_comment}</p>
      )}

      {showReturnForm && (
        <div className="panel"><div className="panel-body">
          <label>Return comment (required)<textarea value={returnComment} onChange={(e) => setReturnComment(e.target.value)} /></label>
          <div className="form-actions">
            <button className="btn-primary" disabled={returnComment.trim().length === 0 || returnMutation.isPending} onClick={() => returnMutation.mutate()}>Return</button>
            <button className="btn-secondary" onClick={() => setShowReturnForm(false)}>Cancel</button>
          </div>
        </div></div>
      )}

      <WorkspaceTabs
        tabs={[
          {
            key: 'document', label: 'Document', content: (
              <div className="panel"><div className="panel-body">
                {c.generated_content ? (
                  <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', maxHeight: 500, overflowY: 'auto' }}>{c.generated_content}</pre>
                ) : (
                  <p className="muted">No document has been generated for this contract yet - it may have been created without a template.</p>
                )}
              </div></div>
            ),
          },
          {
            key: 'approvals', label: 'Approvals', content: (
              <div className="panel"><div className="panel-body">
                <p className="muted">Approval decision history for this contract, resolved through the Approval Matrix.</p>
                <AuditHistoryTab entityType="contract" entityId={contractId} />
              </div></div>
            ),
          },
          {
            key: 'signatures', label: 'Signatures', content: (
              <div className="panel"><div className="panel-body">
                <p className="muted">Both signing parties' real, captured attestations - not blank lines. Neither is filled in until the corresponding action is actually taken.</p>
                <div className="record-header-grid">
                  <div><strong>Employee Signature</strong><br />{c.employee_signature_name ?? 'Not yet signed'}{c.employee_signed_at && ` — ${formatDate(c.employee_signed_at)}`}</div>
                  <div><strong>Employer Signature (Verdantrise)</strong><br />{c.employer_signature_name ?? 'Not yet finalised'}{c.employer_signed_at && ` — ${formatDate(c.employer_signed_at)}`}</div>
                </div>
              </div></div>
            ),
          },
          { key: 'comments', label: 'Comments', content: <CommentsThread entityType="contract" entityId={contractId} /> },
          {
            key: 'documents', label: 'Documents', content: (
              <DocumentsSection
                documents={docsQuery.data?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('contract', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('contract', contractId, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('contract', documentId)}
                queryKeyToInvalidate={['contract-documents', contractId]}
                hrCategories={false}
              />
            ),
          },
          { key: 'tracking', label: 'Tracking', content: <WorkflowTrackingTab entityType="contract" entityId={contractId} actionStageMap={CONTRACT_STAGES} statusLabels={STATUS_LABELS} /> },
        ]}
      />

      {showEditForm && (
        <Modal title="Edit Contract" onClose={() => setShowEditForm(false)}>
          <label>
            Template
            <select value={editValues.templateId} onChange={(e) => setEditValues({ ...editValues, templateId: e.target.value })}>
              <option value="">- no template -</option>
              {templatesQuery.data?.data.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
          <label>Start Date<input type="date" value={editValues.startDate} onChange={(e) => setEditValues({ ...editValues, startDate: e.target.value })} /></label>
          <label>End Date<input type="date" value={editValues.endDate} onChange={(e) => setEditValues({ ...editValues, endDate: e.target.value })} /></label>
          <div className="form-actions">
            <button className="btn-primary" disabled={updateMutation.isPending} onClick={() => updateMutation.mutate()}>{updateMutation.isPending ? 'Saving…' : 'Save Changes'}</button>
            <button className="btn-secondary" onClick={() => setShowEditForm(false)}>Cancel</button>
          </div>
        </Modal>
      )}

      {signatureAction && (
        <Modal title={signatureAction === 'sign' ? 'Sign Contract' : 'Finalise Contract (Employer Signature)'} onClose={closeSignatureModal}>
          <p className="muted">
            {signatureAction === 'sign'
              ? 'Typing your full name below is your electronic signature on this contract. This will be recorded on the document with today\'s date.'
              : 'Typing your full name below confirms this contract on behalf of Verdantrise Humanitarian Initiative. This is the organization\'s counter-signature and will be recorded on the document.'}
          </p>
          <label>
            Type your full name to confirm
            <input value={typedName} onChange={(e) => setTypedName(e.target.value)} placeholder="Full name" />
          </label>
          <div className="form-actions">
            <button type="button" className="btn-primary" disabled={typedName.trim().length < 2 || signMutation.isPending || finaliseMutation.isPending} onClick={submitSignature}>
              {signMutation.isPending || finaliseMutation.isPending ? 'Submitting…' : signatureAction === 'sign' ? 'Sign' : 'Finalise'}
            </button>
            <button type="button" className="btn-secondary" onClick={closeSignatureModal}>Cancel</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
